/**
 * WeekPicker — Visueller Wochen-Selektor (Kalenderwochen pro Jahr).
 *
 * Props:
 *   value: string[]       z.B. ["2026-W15", "2026-W18"]
 *   onChange: (string[]) => void
 *   year?: number         Aktuelles Jahr (default: aktuelles Jahr)
 *   label?: string        Überschrift
 *   helpText?: string     Hinweistext unten
 *
 * Zeigt alle KW eines Jahres als klickbares Grid (13 Spalten × 4 Zeilen).
 * Jahr-Umschalter via Pfeile. Start der Woche: Montag (ISO-Wochen).
 */
import React, { useMemo, useState } from 'react';
import { Button } from './button';
import { Badge } from './badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getISOWeeksInYear, startOfISOWeek, format, addWeeks, setISOWeekYear, setISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';

const WEEKS_PER_ROW = 13;

const getWeekRangeLabel = (year, week) => {
  try {
    let date = setISOWeekYear(new Date(year, 0, 4), year);
    date = setISOWeek(date, week);
    const start = startOfISOWeek(date);
    const end = addWeeks(start, 1);
    const endDay = new Date(end);
    endDay.setDate(endDay.getDate() - 1);
    return `${format(start, 'dd.MM.', { locale: de })} – ${format(endDay, 'dd.MM.', { locale: de })}`;
  } catch {
    return '';
  }
};

export const WeekPicker = ({ value = [], onChange, year: initialYear, label, helpText }) => {
  const [year, setYear] = useState(initialYear || new Date().getFullYear());

  const weeksInYear = useMemo(() => {
    try {
      return getISOWeeksInYear(new Date(year, 5, 15));
    } catch {
      return 52;
    }
  }, [year]);

  const weekKeys = useMemo(() => {
    const keys = [];
    for (let w = 1; w <= weeksInYear; w++) {
      keys.push(`${year}-W${String(w).padStart(2, '0')}`);
    }
    return keys;
  }, [year, weeksInYear]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    // Sort chronologically
    onChange(Array.from(next).sort());
  };

  const selectAll = () => {
    onChange(Array.from(new Set([...value, ...weekKeys])).sort());
  };

  const clearYear = () => {
    onChange(value.filter((k) => !k.startsWith(`${year}-W`)));
  };

  const selectedThisYear = value.filter((k) => k.startsWith(`${year}-W`)).length;

  return (
    <div className="space-y-3" data-testid="week-picker">
      {label && <div className="text-sm font-medium">{label}</div>}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear(year - 1)} data-testid="week-picker-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium text-sm w-16 text-center tabular-nums">{year}</span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear(year + 1)} data-testid="week-picker-next-year">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {selectedThisYear > 0 && (
            <Badge variant="secondary" className="text-xs" data-testid="week-picker-count">
              {selectedThisYear} KW ausgewählt
            </Badge>
          )}
          <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={selectAll}>Alle</Button>
          <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={clearYear}>Jahr leeren</Button>
        </div>
      </div>

      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${WEEKS_PER_ROW}, minmax(0, 1fr))` }}
      >
        {weekKeys.map((key) => {
          const kw = parseInt(key.split('-W')[1], 10);
          const active = selectedSet.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              title={`KW ${kw} · ${getWeekRangeLabel(year, kw)}`}
              className={`h-10 rounded-md text-xs font-medium transition-colors flex flex-col items-center justify-center border ${
                active
                  ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                  : 'bg-muted/30 border-border hover:bg-muted/60'
              }`}
              data-testid={`week-cell-${key}`}
            >
              <span className="leading-none">{kw}</span>
            </button>
          );
        })}
      </div>

      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
    </div>
  );
};

export default WeekPicker;
